/* RESPONSIBLE TEAM: team-ai-insights */
/* eslint-disable @intercom/intercom/no-bare-strings */
import { type SerializedChartSeries } from 'embercom/components/reporting/in-context/static-report';
import type { ColorString } from 'highcharts';

// these colors are copies from apps/teammate-app/src/index.css
// we need to use the values directly here so we can pass them to Highcharts
const colorForNoMetricValue: ColorString = '#eff0eb';
const palette: Record<string, ColorString[]> = {
  salmon: ['#992626', '#bd3e3a', '#de5e54', '#e87e6d', '#ee9d91', '#fabcb2', '#fcdad4'],
  slateblue: ['#0f4abf', '#5447ff', '#6d66ff', '#938aff', '#ada7f6', '#c0baff', '#d9d7fa'],
  cyan: ['#006175', '#0083a1', '#00a7d1', '#44bede', '#63c5de', '#83dcf3', '#beecf7'],
  violet: ['#6d24bd', '#8e33cc', '#b24dd7', '#ca72e9', '#d591ed', '#e1aef2', '#eecdfa'],
};

function buildStops(palette: ColorString[]): Array<[number, ColorString]> {
  return palette.map((color, index) => [index / (palette.length - 1), color]);
}

type MetricConfig = {
  palette: ColorString[];
  colorAxis: { stops: Array<[number, ColorString]> };
  seriesConfig: SerializedChartSeries;
  label: string;
};

type MetricId = keyof typeof metricConfigs;

const metricConfigs: Record<string, MetricConfig> = {
  'conversation.ai_generated_metrics.csat_score': {
    palette: palette.salmon,
    colorAxis: {
      stops: buildStops(palette.salmon),
    },
    seriesConfig: { metric_id: 'conversation.ai_generated_metrics.csat_score' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'CX Score',
  },
  'v2.first_response_time.bot_inbox_included': {
    palette: palette.cyan,
    colorAxis: {
      stops: buildStops(palette.cyan.reverse()),
    },
    seriesConfig: { metric_id: 'v2.first_response_time.bot_inbox_included', aggregation: 'median' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'First response time',
  },
  'v1.handling_time': {
    palette: palette.slateblue,
    colorAxis: {
      stops: buildStops(palette.slateblue.reverse()),
    },
    seriesConfig: {
      metric_id: 'v1.handling_time',
      aggregation: 'median',
      filters: {
        type: 'exists',
        data: {
          property: 'first_reply_teammate_id',
          attribute: 'conversation.first_reply_teammate_id',
        },
      },
    },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'Handling time',
  },
  'fin.resolution_rate': {
    palette: palette.violet,
    colorAxis: {
      stops: buildStops(palette.violet),
    },
    seriesConfig: { metric_id: 'fin.resolution_rate' },
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    label: 'Resolution rate',
  },
} as const;

function addTopicFilter(seriesConfig: SerializedChartSeries, topicId: string) {
  let topicFilter = {
    type: 'category',
    data: {
      attribute: 'conversation_custom_fields#ai_insights_topic',
      property: 'conversation_custom_fields#ai_insights_topic',
      values: [topicId],
    },
  };

  return {
    ...seriesConfig,
    filters: {
      type: 'and',
      filters: [topicFilter, seriesConfig.filters].compact(),
    },
  };
}

export { colorForNoMetricValue, palette, buildStops, metricConfigs, type MetricId, addTopicFilter };
